import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ClientOnboardingWizard from "@/components/ClientOnboardingWizard";
import {
  ArrowRight, Users, Clock, BarChart3, PhoneCall, Globe2, ShieldCheck, CheckCircle2,
} from "lucide-react";

export default function HireRep() {
  const { t, lang } = useI18n();

  const benefits = lang === "el" ? [
    { icon: Globe2, t: "Μιλάει τη γλώσσα της αγοράς", d: "Native ή fluent πωλητές στη χώρα-στόχο σας. Εξαλείφεται το γλωσσικό εμπόδιο, αυξάνεται ο βαθμός εμπιστοσύνης." },
    { icon: Clock, t: "Από 10 ώρες/εβδομάδα", d: "Ξεκινήστε με part-time συνεργασία και αυξήστε τις ώρες μόλις δείτε αποτελέσματα. Καμία μηνιαία δέσμευση." },
    { icon: PhoneCall, t: "Softphone με διεθνή αριθμό", d: "Ο πωλητής χρησιμοποιεί δικό μας browser softphone με αριθμό της χώρας-στόχου. Όλες οι κλήσεις ηχογραφούνται." },
    { icon: BarChart3, t: "Live tracking KPIs", d: "Βλέπετε σε πραγματικό χρόνο: κλήσεις, προτάσεις, deals, έσοδα. Χωρίς surprises στο τέλος του μήνα." },
    { icon: ShieldCheck, t: "Έλεγχος & εγγυήσεις", d: "Κάθε συνεργάτης έχει περάσει vetting. Αν δεν αποδίδει, τον αλλάζουμε χωρίς κόστος." },
    { icon: Users, t: "Multi-market expansion", d: "Προσλάβετε πωλητές σε 2-5 χώρες παράλληλα και επεκταθείτε ταχύτατα χωρίς να στήνετε θυγατρικές." },
  ] : [
    { icon: Globe2, t: "Speaks the local language", d: "Native or fluent reps in your target country. Removes the language barrier, builds trust fast." },
    { icon: Clock, t: "From 10 hrs/week", d: "Start part-time and scale hours once results prove out. No monthly commitment." },
    { icon: PhoneCall, t: "Softphone with international number", d: "Reps use our browser softphone with a target-country number. All calls recorded." },
    { icon: BarChart3, t: "Live KPI tracking", d: "See calls, proposals, deals and revenue in real time. No end-of-month surprises." },
    { icon: ShieldCheck, t: "Vetting & guarantees", d: "Every partner is vetted. If performance lags, we replace them at no cost." },
    { icon: Users, t: "Multi-market expansion", d: "Hire reps in 2-5 countries in parallel and grow without subsidiaries." },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="hire-rep-page">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-[#0052CC] flex items-center justify-center text-white font-bold">GE</div>
            <span className="font-heading font-bold text-slate-900">{t("app.name")}</span>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
            <Link to="/hire-rep" className="text-[#0052CC] font-semibold">{lang === "el" ? "Πρόσληψη Πωλητή Εξωτερικού" : "Hire Sales Rep"}</Link>
            <Link to="/directories" className="hover:text-slate-900">{lang === "el" ? "Επαγγελματικοί Κατάλογοι" : "Business Directories"}</Link>
            <Link to="/contact" className="hover:text-slate-900">{lang === "el" ? "Επικοινωνία" : "Contact"}</Link>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/login" className="px-4 py-2 text-sm font-semibold bg-[#0052CC] text-white rounded-md hover:bg-[#0043A8]">
              {t("auth.login")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0A1628] via-[#0F1E3D] to-[#0052CC] text-white">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-emerald-500/20 blur-[120px]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs uppercase tracking-[0.25em] font-semibold mb-6">
            <Users className="w-3.5 h-3.5" /> {lang === "el" ? "Υπηρεσία πωλήσεων εξωτερικού" : "Foreign sales service"}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl">
            {lang === "el"
              ? "Προσλάβετε έμπειρο πωλητή στη χώρα-στόχο σας"
              : "Hire an experienced sales rep in your target country"}
          </h1>
          <p className="mt-6 text-lg lg:text-xl text-slate-200 max-w-3xl leading-relaxed">
            {lang === "el"
              ? "Native πωλητές που γνωρίζουν την αγορά, μιλούν τη γλώσσα και προωθούν ενεργά τα προϊόντα σας. Νοικιάζετε ανά ώρα — πληρώνετε μόνο αυτά που δουλεύει."
              : "Native sales reps who know the market, speak the language and actively promote your products. Rent by the hour — pay only what they work."}
          </p>
          <a
            href="#start"
            data-testid="hire-rep-start-cta"
            className="mt-10 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-slate-900 rounded-md font-semibold hover:bg-slate-100"
          >
            {lang === "el" ? "Ξεκινήστε τώρα" : "Get started"} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-white py-16 lg:py-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-10">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#0052CC] mb-3">
              {lang === "el" ? "Γιατί να μας επιλέξετε" : "Why choose us"}
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              {lang === "el" ? "Μια επαγγελματική ομάδα πωλήσεων — χωρίς το ρίσκο της πρόσληψης" : "A professional sales team — without hiring risk"}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md hover:border-[#0052CC]/30 transition-all">
                <div className="w-11 h-11 rounded-lg bg-[#0052CC]/10 text-[#0052CC] flex items-center justify-center mb-4">
                  <b.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-bold text-slate-900">{b.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wizard */}
      <section id="start" className="bg-[#F8FAFC] py-16 lg:py-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-10">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#10B981] mb-3">
              {lang === "el" ? "Βήμα-βήμα" : "Step by step"}
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              {lang === "el" ? "Πείτε μας τι ψάχνετε" : "Tell us what you're looking for"}
            </h2>
            <p className="mt-4 text-slate-600">
              {lang === "el"
                ? "6 γρήγορα βήματα και η ομάδα μας αναλαμβάνει να σας προτείνει τους καλύτερους πωλητές για την αγορά σας."
                : "6 quick steps. Our team will analyze your needs and recommend the best sales partners for your market."}
            </p>
          </div>
          <ClientOnboardingWizard />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-10 text-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>© 2026 {t("app.name")}</div>
          <div className="flex gap-6">
            <Link to="/hire-rep" className="hover:text-white">{lang === "el" ? "Πρόσληψη Πωλητή" : "Hire Rep"}</Link>
            <Link to="/directories" className="hover:text-white">{lang === "el" ? "Κατάλογοι" : "Directories"}</Link>
            <Link to="/contact" className="hover:text-white">{lang === "el" ? "Επικοινωνία" : "Contact"}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
